// src/components/grid/TeacherLoadPanel.tsx
// FIXED VERSION: Properly displays allowance deductions in teacher loads

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Search, X, Info, Clock, ArrowUpDown, Check, Users, MessageSquare } from 'lucide-react';
import { Staff } from '../../contexts/DataContext';
import { StaffAllocationGrid } from '../../types/grid';
import { useStabilizedAllocation, useStabilizedGrid } from '../../contexts/StabilizedContexts';
import { useMultiGrid } from '../../hooks/useMultiGrid';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { getAllowancesForSemester, getTotalAllowanceMinutes } from '../../types/staff';
import { TeachingIntention } from '../../types/intentions';
import { CurriculumSubject } from '../../utils/preferenceMatching';
import AdditionalCommentsModal from '../intentions/AdditionalCommentsModal';
import { supabase } from '../../supabase/config';
import { assignmentEvents } from '../../utils/assignmentEvents';
import { DEFAULT_BASE_MINUTES_PER_FORTNIGHT, DEFAULT_MINUTES_PER_LINE } from '../../types/schoolConfig';

interface TeacherLoadPanelProps {
  staff: Staff[];
  grid: StaffAllocationGrid | null;
  activeSemester?: 'both' | 'semester1' | 'semester2';
  teachingIntentions?: TeachingIntention[];
  curriculumSubjects?: CurriculumSubject[];
  allGrids?: StaffAllocationGrid[]; // NEW: Support for multi-grid calculations
}

type SortOption = 'name' | 'preference' | 'utilization' | 'availability';

// Helper function to create stable hash for assignment data
const createAssignmentDataHash = (grids: StaffAllocationGrid[]): string => {
  return grids.map(grid => {
    const assignmentData = grid.classes?.map(c => {
      const assignments = c.lineAssignments || {};
      return `${c.id}:${Object.entries(assignments).sort().map(([line, teacher]) => `${line}=${teacher}`).join(',')}`;
    }).join('|') || '';
    return `${grid.id}[${assignmentData}]`;
  }).join(';;');
};

const TeacherLoadPanel: React.FC<TeacherLoadPanelProps> = ({
  staff = [],
  grid,
  activeSemester = 'both',
  teachingIntentions = [],
  curriculumSubjects = [],
  allGrids = []
}) => {
  // FIXED: Add internal semester control state
  const [internalActiveSemester, setInternalActiveSemester] = useState(activeSemester);

  // FIXED: Control whether to show semester controls based on whether activeSemester prop is provided
  const showSemesterControls = activeSemester === undefined;

  // Use the stabilized allocation context for multi-grid support
  const {
    getTeacherSemesterLoad,
    onAssignmentChange
  } = useStabilizedAllocation();

  // Get updated grid data from stabilized context to ensure real-time updates
  const {
    grids: stabilizedGrids,
    activeGrid: stabilizedActiveGrid,
    loading: stabilizedGridsLoading
  } = useStabilizedGrid();

  // 🚀 REAL-TIME FIX: Get updated grids from MultiGridContext for immediate updates
  const {
    allGrids: multiGridContextGrids,
    currentGrid: multiGridCurrentGrid
  } = useMultiGrid();

  // Loads are per academic year — see the year filter on gridsToProcess below.
  const { workingYear } = useAcademicYear();

  // Use the correct semester value based on whether we're controlled or uncontrolled
  const effectiveSemester = showSemesterControls ? internalActiveSemester : activeSemester;

  // FIXED: Handle semester change properly
  const handleSemesterChange = (newSemester: 'both' | 'semester1' | 'semester2') => {
    if (showSemesterControls) {
      setInternalActiveSemester(newSemester);
    }
  };

  // Multi-select search functionality
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
  const [showAllowanceDetails, setShowAllowanceDetails] = useState(false);
  
  // Additional comments modal state
  const [commentsModal, setCommentsModal] = useState<{
    isOpen: boolean;
    staffName: string;
    staffInitial: string;
    comments: string;
  }>({
    isOpen: false,
    staffName: '',
    staffInitial: '',
    comments: ''
  });
  
  // Sorting functionality
  const [sortBy, setSortBy] = useState<SortOption>('name');

  // Real-time update trigger state
  const [realtimeUpdateTrigger, setRealtimeUpdateTrigger] = useState<number>(0);
  const subscriptionRef = useRef<any>(null);

  // Set up global assignment change listener for instant real-time updates
  useEffect(() => {
    const cleanup = assignmentEvents.subscribe(() => {
      setRealtimeUpdateTrigger(prev => prev + 1);
    });

    // Also set up the original StabilizedAllocation callback if available (for backward compatibility)
    let stabilizedCleanup: (() => void) | undefined;
    if (onAssignmentChange) {
      stabilizedCleanup = onAssignmentChange(() => {
        setRealtimeUpdateTrigger(prev => prev + 1);
      });
    }

    return () => {
      cleanup();
      if (stabilizedCleanup) stabilizedCleanup();
    };
  }, [onAssignmentChange]);

  // Trigger initial updates when grid data becomes available
  useEffect(() => {
    if (stabilizedGrids?.length > 0 || allGrids?.length > 0) {
      setRealtimeUpdateTrigger(prev => prev + 1);
    }
  }, [
    // React to initial grid data availability
    stabilizedGrids?.length,
    allGrids?.length,
    stabilizedGridsLoading
  ]);

  // Memoize stable versions of input arrays to prevent unnecessary recalculations
  const stableStaff = useMemo(() => staff, [staff?.length, staff?.map(s => s.staff_id || s.initial).join(',')]);
  const stableIntentions = useMemo(() => teachingIntentions, [teachingIntentions?.length]);
  const stableCurriculumSubjects = useMemo(() => curriculumSubjects, [curriculumSubjects?.length]);

  // Calculate preference scores for sorting
  const calculatePreferenceScore = useMemo(() => {
    const teacherScores = new Map<string, number>();
    
    if (!stableIntentions.length && !stableCurriculumSubjects.length) {
      return teacherScores;
    }

    stableStaff.forEach(teacher => {
      let score = 0;
      
      // Check teaching intentions (self-nominated preferences)
      const intention = stableIntentions.find(i => i.staffId === (teacher.staff_id || teacher.initial));
      if (intention) {
        // Count high-priority preferences (lower rank = higher priority)
        const highPriorityPrefs = intention.teachingPreferences?.filter(p => p.rank <= 3) || [];
        score += highPriorityPrefs.length * 3; // High priority preferences worth 3 points each
        
        const mediumPriorityPrefs = intention.teachingPreferences?.filter(p => p.rank > 3 && p.rank <= 6) || [];
        score += mediumPriorityPrefs.length * 2; // Medium priority preferences worth 2 points each
        
        const lowPriorityPrefs = intention.teachingPreferences?.filter(p => p.rank > 6) || [];
        score += lowPriorityPrefs.length * 1; // Low priority preferences worth 1 point each
        
        // Bonus for comfortable teaching areas
        if (intention.comfortableTeachingAreas?.length) {
          score += intention.comfortableTeachingAreas.length * 0.5;
        }
      }
      
      // Check curriculum nominations (nominated by curriculum leaders)
      stableCurriculumSubjects.forEach(subject => {
        const nomination = subject.teacherPreferences?.find(p => p.teacherCode === (teacher.staff_id || teacher.initial));
        if (nomination) {
          switch (nomination.preferenceLevel) {
            case 'high':
              score += 4; // High curriculum nomination worth 4 points
              break;
            case 'medium':
              score += 2;
              break;
            case 'low':
              score += 1;
              break;
            // 'avoid' adds 0 points
          }
        }
      });
      
      teacherScores.set(teacher.staff_id || teacher.initial, score);
    });
    
    return teacherScores;
  }, [stableStaff, stableIntentions, stableCurriculumSubjects]);

  // Calculate teacher loads from current grid and all grids (multi-grid support)
  const teacherLoads = useMemo(() => {
    if (!staff) return []; // Early return if staff not available

    // Every grid source below carries EVERY academic year (GridsDataContext fetches
    // the whole grids table), so scope each one to the year being planned before the
    // "does this source have classes?" test — otherwise last year's fully-assigned
    // grid always wins the race and its minutes are reported as this year's load
    // (issue #165). A grid with no academic_year is kept rather than dropped.
    const forYear = (gs: StaffAllocationGrid[]) =>
      gs.filter(g => g && (g.year == null || g.year === workingYear));

    const stabilizedForYear = forYear(stabilizedGrids);
    const multiGridForYear = forYear(multiGridContextGrids);
    const allGridsForYear = forYear(allGrids);
    const activeForYear = forYear(stabilizedActiveGrid ? [stabilizedActiveGrid] : []);
    const propGridForYear = forYear(grid ? [grid] : []);

    // 🚀 REAL-TIME FIX: Smart data source selection based on which context has the freshest data
    // Since you're using StabilizedAllocation, prioritize stabilizedGrids over multiGridContextGrids
    const gridsToProcess =
      (stabilizedForYear.length > 0 && stabilizedForYear.some(g => g.classes && g.classes.length > 0)) ? stabilizedForYear :
      (multiGridForYear.length > 0 && multiGridForYear.some(g => g.classes && g.classes.length > 0)) ? multiGridForYear :
      (allGridsForYear.length > 0 && allGridsForYear.some(g => g.classes && g.classes.length > 0)) ? allGridsForYear :
      (activeForYear.length > 0 && activeForYear.some(g => g.classes && g.classes.length > 0)) ? activeForYear :
      (propGridForYear.length > 0 && propGridForYear.some(g => g.classes && g.classes.length > 0)) ? propGridForYear :
      // Fallback: use any available grid data even if classes are empty (for new grids)
      stabilizedForYear.length > 0 ? stabilizedForYear :
      multiGridForYear.length > 0 ? multiGridForYear :
      allGridsForYear.length > 0 ? allGridsForYear :
      activeForYear.length > 0 ? activeForYear :
      propGridForYear;

    return staff.map(teacher => {
      // FIXED: Ensure teacher staff_id or initial exists
      const teacherInitial = teacher.staff_id || teacher.initial || '';

      // Get allowances for display
      const semester1Allowances = getAllowancesForSemester(teacher, 'semester1', workingYear);
      const semester2Allowances = getAllowancesForSemester(teacher, 'semester2', workingYear);
      const semester1AllowanceMinutes = getTotalAllowanceMinutes(teacher, 'semester1', workingYear);
      const semester2AllowanceMinutes = getTotalAllowanceMinutes(teacher, 'semester2', workingYear);

      // Calculate loads directly from all grid assignments
      const semester1Assignments: any[] = [];
      const semester2Assignments: any[] = [];
      const gridBreakdown: { gridName: string; semester1Minutes: number; semester2Minutes: number; }[] = [];

      // Count assignments from all grids - this will reflect real-time changes
      gridsToProcess.forEach(currentGrid => {
        if (currentGrid && currentGrid.classes) {
          let gridSem1Minutes = 0;
          let gridSem2Minutes = 0;


          // Filter out composite sub-classes to avoid double-counting minutes
          currentGrid.classes
            .filter(classItem => !classItem.isCompositeSubClass)
            .forEach(classItem => {
              Object.entries(classItem.lineAssignments || {}).forEach(([lineId, assignedTeacher]) => {

              // Handle multi-teacher assignments (stored as JSON string)
              let teacherMinutes = 0;
              let isAssignedToThisTeacher = false;
              
              if (assignedTeacher) {
                // Check if it's a JSON string (multi-teacher assignment)
                if (assignedTeacher.startsWith('[') || assignedTeacher.startsWith('{')) {
                  try {
                    const assignments = JSON.parse(assignedTeacher);
                    const teacherAssignment = Array.isArray(assignments) 
                      ? assignments.find((a: any) => a.teacherCode === teacherInitial)
                      : (assignments.teacherCode === teacherInitial ? assignments : null);
                    
                    if (teacherAssignment) {
                      isAssignedToThisTeacher = true;
                      teacherMinutes = teacherAssignment.minutes || DEFAULT_MINUTES_PER_LINE; // Default to full load if not specified
                    }
                  } catch (e) {
                    // If parsing fails, treat as single teacher
                    if (assignedTeacher === teacherInitial) {
                      isAssignedToThisTeacher = true;
                      teacherMinutes = classItem.minutesPerWeek || currentGrid.defaultClassMinutes || 360;
                    }
                  }
                } else {
                  // Single teacher assignment
                  if (assignedTeacher === teacherInitial) {
                    isAssignedToThisTeacher = true;
                    teacherMinutes = classItem.minutesPerWeek || currentGrid.defaultClassMinutes || 360;
                  }
                }
              }
              
              if (isAssignedToThisTeacher && teacherMinutes > 0) {
                const line = currentGrid.lines.find(l => l.id === lineId);

                if (line) {
                  const assignment = {
                    className: classItem.className,
                    lineId: lineId,
                    minutesPerWeek: teacherMinutes, // Use the specific teacher's minutes
                    gridName: currentGrid.name,
                    gridType: `${currentGrid.defaultClassMinutes || ''}min classes`
                  };

                  // Determine semester - use line.semester if available, otherwise infer from line ID
                  let semester = line.semester;
                  if (!semester) {
                    // Fallback: infer semester from common line naming patterns
                    // Lines A-F typically semester 1, G-L typically semester 2
                    const semester1Lines = ['A', 'B', 'C', 'D', 'E', 'F'];
                    const semester2Lines = ['G', 'H', 'I', 'J', 'K', 'L'];

                    if (semester1Lines.includes(lineId)) {
                      semester = 'semester1';
                    } else if (semester2Lines.includes(lineId)) {
                      semester = 'semester2';
                    } else {
                      // Default to semester1 if can't determine
                      semester = 'semester1';
                    }
                  }

                  if (semester === 'semester1') {
                    semester1Assignments.push(assignment);
                    gridSem1Minutes += teacherMinutes;
                  } else if (semester === 'semester2') {
                    semester2Assignments.push(assignment);
                    gridSem2Minutes += teacherMinutes;
                  }
                }
              }
            });
          });

          // Add to grid breakdown if teacher has assignments in this grid
          if (gridSem1Minutes > 0 || gridSem2Minutes > 0) {
            gridBreakdown.push({
              gridName: currentGrid.name,
              semester1Minutes: gridSem1Minutes,
              semester2Minutes: gridSem2Minutes
            });
          }
        }
      });

      // Calculate semester loads from actual assignments
      const semester1Minutes = semester1Assignments.reduce((total, assignment) => total + assignment.minutesPerWeek, 0);
      const semester2Minutes = semester2Assignments.reduce((total, assignment) => total + assignment.minutesPerWeek, 0);

      
      // Calculate available minutes (FTE × base minutes per semester - allowances)
      const availableMinutesPerSemester = (teacher.fte * DEFAULT_BASE_MINUTES_PER_FORTNIGHT);
      const semester1AvailableMinutes = availableMinutesPerSemester - semester1AllowanceMinutes;
      const semester2AvailableMinutes = availableMinutesPerSemester - semester2AllowanceMinutes;

      // Calculate utilization percentages
      const semester1Utilization = semester1AvailableMinutes > 0 ? Math.round((semester1Minutes / semester1AvailableMinutes) * 100) : 0;
      const semester2Utilization = semester2AvailableMinutes > 0 ? Math.round((semester2Minutes / semester2AvailableMinutes) * 100) : 0;

      const semester1Load = {
        allocatedMinutes: semester1Minutes,
        availableMinutes: semester1AvailableMinutes,
        utilization: semester1Utilization,
        assignments: semester1Assignments,
        allowances: semester1Allowances,
        allowanceMinutes: semester1AllowanceMinutes
      };

      const semester2Load = {
        allocatedMinutes: semester2Minutes,
        availableMinutes: semester2AvailableMinutes,
        utilization: semester2Utilization,
        assignments: semester2Assignments,
        allowances: semester2Allowances,
        allowanceMinutes: semester2AllowanceMinutes
      };

      return {
        ...teacher,
        semester1: semester1Load,
        semester2: semester2Load,
        gridBreakdown: gridBreakdown, // NEW: Per-grid breakdown for multi-grid view
        totalGrids: gridBreakdown.length, // NEW: Number of grids this teacher is assigned to
        isFullyAssigned: semester1Utilization >= 100 && semester2Utilization >= 100,
        isActive: true,
        preferenceScore: calculatePreferenceScore.get(teacherInitial) || 0
      };
    });
  }, [
      // Core data dependencies
      staff, calculatePreferenceScore,

      // Academic year being planned — scopes which grids count towards the load
      workingYear,

      // Grid identification dependencies (stable)
      grid?.id,
      allGrids?.map(g => g.id).join(','),
      stabilizedActiveGrid?.id,
      stabilizedGrids?.map(g => g.id).join(','),

      // 🚀 REAL-TIME FIX: MultiGridContext dependencies for immediate updates
      multiGridContextGrids?.map(g => g.id).join(','),
      multiGridCurrentGrid?.id,

      // Assignment data change detection (stable hash) - prioritize StabilizedGrids
      createAssignmentDataHash(
        (stabilizedGrids.length > 0 && stabilizedGrids.some(g => g.classes && g.classes.length > 0)) ? stabilizedGrids :
        (multiGridContextGrids.length > 0 && multiGridContextGrids.some(g => g.classes && g.classes.length > 0)) ? multiGridContextGrids :
        (allGrids.length > 0 && allGrids.some(g => g.classes && g.classes.length > 0)) ? allGrids :
        (stabilizedActiveGrid && stabilizedActiveGrid.classes && stabilizedActiveGrid.classes.length > 0) ? [stabilizedActiveGrid] :
        (grid && grid.classes && grid.classes.length > 0) ? [grid] : []
      ),

      // Additional trigger for stabilized grid changes
      stabilizedGrids?.map(g => `${g.id}:${g.classes?.length || 0}`).join('|'),
      stabilizedActiveGrid && `${stabilizedActiveGrid.id}:${stabilizedActiveGrid.classes?.length || 0}`,

      // 🚀 REAL-TIME FIX: MultiGridContext change triggers for immediate updates
      multiGridContextGrids?.map(g => `${g.id}:${g.classes?.length || 0}`).join('|'),
      multiGridCurrentGrid && `${multiGridCurrentGrid.id}:${multiGridCurrentGrid.classes?.length || 0}`,

      // Real-time update trigger (replaces Math.random)
      realtimeUpdateTrigger,

      // Loading state dependencies
      stabilizedGridsLoading
    ]);

  // Sort teachers based on selected sort option
  const sortedTeacherLoads = useMemo(() => {
    return [...teacherLoads].sort((a, b) => {
      switch (sortBy) {
        case 'preference':
          // Higher preference scores first, then by name
          if (a.preferenceScore !== b.preferenceScore) {
            return b.preferenceScore - a.preferenceScore;
          }
          return a.fullName.localeCompare(b.fullName);
        
        case 'utilization':
          // Calculate overall utilization (average of both semesters)
          const aUtil = effectiveSemester === 'semester1' ? a.semester1.utilization :
                        effectiveSemester === 'semester2' ? a.semester2.utilization :
                        (a.semester1.utilization + a.semester2.utilization) / 2;
          const bUtil = effectiveSemester === 'semester1' ? b.semester1.utilization :
                        effectiveSemester === 'semester2' ? b.semester2.utilization :
                        (b.semester1.utilization + b.semester2.utilization) / 2;
          if (aUtil !== bUtil) {
            return bUtil - aUtil; // Higher utilization first
          }
          return a.fullName.localeCompare(b.fullName);
        
        case 'availability':
          // Calculate overall available minutes
          const aAvail = effectiveSemester === 'semester1' ? a.semester1.availableMinutes :
                         effectiveSemester === 'semester2' ? a.semester2.availableMinutes :
                         a.semester1.availableMinutes + a.semester2.availableMinutes;
          const bAvail = effectiveSemester === 'semester1' ? b.semester1.availableMinutes :
                         effectiveSemester === 'semester2' ? b.semester2.availableMinutes :
                         b.semester1.availableMinutes + b.semester2.availableMinutes;
          if (aAvail !== bAvail) {
            return bAvail - aAvail; // More available minutes first
          }
          return a.fullName.localeCompare(b.fullName);
        
        case 'name':
        default:
          return a.fullName.localeCompare(b.fullName);
      }
    });
  }, [teacherLoads, sortBy, effectiveSemester]);

  // Get available teachers for search dropdown
  const searchableTeachers = useMemo(() => {
    if (!searchQuery) return [];
    const searchLower = searchQuery.toLowerCase();
    return sortedTeacherLoads.filter(teacher =>
      !selectedTeachers.includes(teacher.staff_id || teacher.initial) &&
      (teacher.fullName?.toLowerCase().includes(searchLower) ||
       (teacher.staff_id || teacher.initial)?.toLowerCase().includes(searchLower) ||
       teacher.department?.toLowerCase().includes(searchLower))
    ).slice(0, 5); // Limit to 5 results for dropdown
  }, [sortedTeacherLoads, searchQuery, selectedTeachers]);

  // Filter teachers based on semester view and selected teachers
  const filteredTeachers = useMemo(() => {
    if (selectedTeachers.length > 0) {
      // Show ONLY selected teachers, ignore any search query
      return sortedTeacherLoads.filter(teacher => selectedTeachers.includes(teacher.staff_id || teacher.initial));
    }
    // Show all teachers if none selected (also ignore search query for main list)
    return sortedTeacherLoads;
  }, [sortedTeacherLoads, selectedTeachers]);

  // Filter teachers based on assignment status
  const activeTeachers = filteredTeachers.filter(t => !t.isFullyAssigned);
  const fullyAssignedTeachers = filteredTeachers.filter(t => t.isFullyAssigned);
  const [showFullyAssigned, setShowFullyAssigned] = useState(false);

  // Get utilization color based on percentage
  const getUtilizationColor = (utilization: number) => {
    if (utilization > 100) return 'bg-danger-solid';
    if (utilization > 90) return 'bg-warn-solid';
    return 'bg-ok-solid';
  };

  // Get text color based on utilization
  const getUtilizationTextColor = (utilization: number) => {
    if (utilization > 100) return 'text-danger-fg';
    if (utilization > 90) return 'text-warn-fg';
    return 'text-ok-fg';
  };

  // Get semester display title
  const getSemesterTitle = () => {
    switch (effectiveSemester) {
      case 'semester1':
        return 'Semester 1 Loads';
      case 'semester2':
        return 'Semester 2 Loads';
      default:
        return 'Teacher Loads';
    }
  };

  // Get stats text
  const getStatsText = () => {
    const activeCount = activeTeachers.length;
    const fullyAssignedCount = fullyAssignedTeachers.length;
    if (effectiveSemester === 'both') {
      return `${activeCount} available • ${fullyAssignedCount} fully assigned`;
    } else {
      const semesterName = effectiveSemester === 'semester1' ? 'S1' : 'S2';
      return `${activeCount} available in ${semesterName} • ${fullyAssignedCount} fully assigned`;
    }
  };

  // Handle search toggle
  const toggleSearch = () => {
    setIsSearchExpanded(!isSearchExpanded);
    if (isSearchExpanded) {
      setSearchQuery('');
      setSelectedTeachers([]);
    }
  };

  // Add teacher to selection
  const addTeacherToSelection = (teacherInitial: string) => {
    if (!selectedTeachers.includes(teacherInitial)) {
      setSelectedTeachers([...selectedTeachers, teacherInitial]);
    }
    setSearchQuery(''); // Clear search after selection
  };

  // Remove teacher from selection
  const removeTeacherFromSelection = (teacherInitial: string) => {
    setSelectedTeachers(selectedTeachers.filter(t => t !== teacherInitial));
  };

  // Clear all selected teachers
  const clearAllSelections = () => {
    setSelectedTeachers([]);
  };

  // Get teaching intention for a staff member
  const getTeachingIntention = (staffId: string): TeachingIntention | undefined => {
    return stableIntentions.find(intention => intention.staffId === staffId);
  };

  // Check if staff member has additional comments
  const hasAdditionalComments = (staffId: string): boolean => {
    const intention = getTeachingIntention(staffId);
    return !!(intention?.additionalComments && intention.additionalComments.trim().length > 0);
  };

  // Show additional comments modal
  const showAdditionalComments = (staffId: string, staffName: string) => {
    const intention = getTeachingIntention(staffId);
    if (intention?.additionalComments) {
      setCommentsModal({
        isOpen: true,
        staffName,
        staffInitial: staffId,
        comments: intention.additionalComments
      });
    }
  };

  // Close additional comments modal
  const closeCommentsModal = () => {
    setCommentsModal({
      isOpen: false,
      staffName: '',
      staffInitial: '',
      comments: ''
    });
  };
  return <div className="w-96 bg-white h-full overflow-y-auto flex flex-col">
      <div className="p-4 border-b border-ink-200 bg-ink-50 flex-shrink-0">
        <h3 className="text-lg font-medium text-ink-900">
          {getSemesterTitle()}
        </h3>
        <div className="flex justify-between items-center">
          {!isSearchExpanded ? (
            selectedTeachers.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-xs text-navy-600 font-medium">
                  {selectedTeachers.length} teacher{selectedTeachers.length > 1 ? 's' : ''} selected
                </span>
                {selectedTeachers.slice(0, 2).map(teacherInitial => {
                  const teacher = staff.find(t => t.initial === teacherInitial);
                  return (
                    <span key={teacherInitial} className="font-mono tabular-nums inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-navy-100 text-navy-800">
                      {teacher?.initial || teacherInitial}
                    </span>
                  );
                })}
                {selectedTeachers.length > 2 && (
                  <span className="text-xs text-ink-500">+{selectedTeachers.length - 2} more</span>
                )}
              </div>
            ) : (
              stabilizedGridsLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border border-navy-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-navy-600">Loading assignment data...</span>
                </div>
              ) : (
                <p className="text-sm text-ink-500">{getStatsText()}</p>
              )
            )
          ) : (
            <div className="relative flex-1 mr-2">
              <input 
                type="text" 
                placeholder="Search to add teachers..." 
                className="w-full pl-8 pr-8 py-1 text-sm border border-ink-300 rounded-md focus:outline-none focus:ring-1 focus:ring-navy-500" 
                value={searchQuery} 
                onChange={e => setSearchQuery(e.target.value)} 
                autoFocus 
              />
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ink-400" />
              {searchQuery && (
                <button 
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-ink-400 hover:text-ink-500" 
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              
              {/* Search dropdown */}
              {searchQuery && searchableTeachers.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-ink-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {searchableTeachers.map(teacher => (
                    <button
                      key={teacher.staff_id || teacher.initial}
                      className="w-full px-3 py-2 text-left hover:bg-navy-50 flex items-center justify-between group"
                      onClick={() => addTeacherToSelection(teacher.staff_id || teacher.initial)}
                    >
                      <div>
                        <div className="text-sm font-medium text-ink-900">
                          {teacher.staff_id || teacher.initial} - {teacher.fullName}
                        </div>
                        <div className="text-xs text-ink-500">
                          {teacher.department}
                        </div>
                      </div>
                      <Check className="h-4 w-4 text-navy-500 opacity-0 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}
              
              {/* No results message */}
              {searchQuery && searchableTeachers.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-ink-300 rounded-md shadow-lg p-3">
                  <div className="text-sm text-ink-500 text-center">
                    No teachers found matching "{searchQuery}"
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center space-x-2">
            {/* Multi-select search toggle */}
            <button 
              onClick={toggleSearch} 
              className={`p-1 rounded-md relative ${isSearchExpanded ? 'bg-navy-100 text-navy-600' : selectedTeachers.length > 0 ? 'bg-navy-100 text-navy-600' : 'text-ink-500 hover:text-ink-700 hover:bg-ink-100'}`} 
              title={selectedTeachers.length > 0 ? `${selectedTeachers.length} teachers selected` : "Multi-select teachers"}
            >
              <Users className="h-4 w-4" />
              {selectedTeachers.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-navy-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {selectedTeachers.length}
                </span>
              )}
            </button>
            
            {/* Clear selection button when teachers are selected */}
            {selectedTeachers.length > 0 && (
              <button 
                onClick={clearAllSelections}
                className="p-1 rounded-md text-ink-500 hover:text-danger-fg hover:bg-danger-bg" 
                title="Clear teacher selection"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            
            {/* Sort dropdown */}
            <div className="relative">
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="appearance-none bg-white border border-ink-300 rounded-md px-3 py-1 pr-8 text-xs focus:outline-none focus:ring-1 focus:ring-navy-500 focus:border-navy-500"
                title="Sort teachers by"
              >
                <option value="name">Name</option>
                <option value="preference">Preferences</option>
                <option value="utilization">Utilization</option>
                <option value="availability">Availability</option>
              </select>
              <ArrowUpDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-ink-400 pointer-events-none" />
            </div>
            
            {/* Show allowance details toggle */}
            <button onClick={() => setShowAllowanceDetails(!showAllowanceDetails)} className={`p-1 rounded-md ${showAllowanceDetails ? 'bg-navy-100 text-navy-600' : 'text-ink-500 hover:text-ink-700 hover:bg-ink-100'}`} title="Show allowance details">
              <Info className="h-4 w-4" />
            </button>
            
            {/* Semester filter controls - only show if not externally controlled */}
            {showSemesterControls && <div className="flex space-x-1">
                <button className={`px-2 py-1 text-xs rounded transition-colors ${effectiveSemester === 'both' ? 'bg-navy-100 text-navy-800 font-medium' : 'bg-ink-100 text-ink-500 hover:bg-ink-200'}`} onClick={() => handleSemesterChange('both')}>
                  Both
                </button>
                <button className={`px-2 py-1 text-xs rounded transition-colors ${effectiveSemester === 'semester1' ? 'bg-navy-100 text-navy-800 font-medium' : 'bg-ink-100 text-ink-500 hover:bg-ink-200'}`} onClick={() => handleSemesterChange('semester1')}>
                  S1
                </button>
                <button className={`px-2 py-1 text-xs rounded transition-colors ${effectiveSemester === 'semester2' ? 'bg-navy-100 text-navy-800 font-medium' : 'bg-ink-100 text-ink-500 hover:bg-ink-200'}`} onClick={() => handleSemesterChange('semester2')}>
                  S2
                </button>
              </div>}
          </div>
        </div>
        {/* Selected teachers indicator */}
        {selectedTeachers.length > 0 && (
          <div className="mt-2">
            <div className="flex flex-wrap gap-1">
              {selectedTeachers.map(teacherInitial => {
                const teacher = staff.find(t => t.initial === teacherInitial);
                return (
                  <span key={teacherInitial} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-navy-100 text-navy-800 gap-1">
                    {teacher?.initial} - {teacher?.fullName}
                    <button 
                      onClick={() => removeTeacherFromSelection(teacherInitial)}
                      className="hover:bg-navy-200 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
            <div className="text-xs text-ink-500 mt-1">
              Showing {filteredTeachers.length} selected teacher{filteredTeachers.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Active Teachers */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <h4 className="text-sm font-medium text-ink-700 mb-3">
            {selectedTeachers.length > 0 ? 'Selected Teachers' : 'Available for Assignment'}
          </h4>
          <div className="space-y-3">
            {activeTeachers.map((teacher, index) => <div key={`active-${teacher.id || teacher.staff_id || teacher.initial || index}`} className="border rounded-lg p-3 bg-ink-50 hover:bg-ink-100 transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-ink-900 truncate flex items-center gap-2">
                      {teacher.staff_id || teacher.initial} - {teacher.fullName}
                      {hasAdditionalComments(teacher.staff_id || teacher.initial) && (
                        <button
                          onClick={() => showAdditionalComments(teacher.staff_id || teacher.initial, teacher.fullName)}
                          className="p-1 hover:bg-navy-100 rounded-full transition-colors group"
                          title="View additional comments"
                        >
                          <MessageSquare className="h-4 w-4 text-navy-600 group-hover:text-navy-700" />
                        </button>
                      )}
                      {sortBy === 'preference' && teacher.preferenceScore > 0 && (
                        <span className={`font-mono tabular-nums inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          teacher.preferenceScore >= 10 ? 'bg-ok-bg text-ok-fg' :
                          teacher.preferenceScore >= 5 ? 'bg-warn-bg text-warn-fg' :
                          'bg-ink-100 text-ink-900'
                        }`} title={`Preference score: ${teacher.preferenceScore}`}>
                          {teacher.preferenceScore}★
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-500">
                      {teacher.department}
                      {teacher.totalGrids > 1 && (
                        <span className="font-mono tabular-nums ml-2 px-2 py-0.5 bg-navy-100 text-navy-700 rounded-full text-xs">
                          {teacher.totalGrids} grids
                        </span>
                      )}
                      {sortBy === 'preference' && teacher.preferenceScore === 0 && (
                        <span className="text-ink-400 ml-2">No preferences</span>
                      )}
                    </div>
                    {teacher.gridBreakdown && teacher.gridBreakdown.length > 1 && (
                      <div className="text-xs text-ink-500 mt-1">
                        <div className="flex flex-wrap gap-1">
                          {teacher.gridBreakdown.map((gridData, idx) => (
                            <span key={idx} className="px-1 py-0.5 bg-ink-100 rounded text-xs">
                              {gridData.gridName}: {gridData.semester1Minutes + gridData.semester2Minutes}min
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Semester 1 Section */}
                {(effectiveSemester === 'both' || effectiveSemester === 'semester1') && <div className={effectiveSemester === 'both' ? 'mb-3' : ''}>
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-xs font-medium text-ink-700">
                        {effectiveSemester === 'both' ? 'Semester 1' : 'Current Load'}
                      </div>
                      <div className="text-xs">
                        <span className={`font-mono tabular-nums ${getUtilizationTextColor(teacher.semester1.utilization)}`}>
                          {teacher.semester1.utilization}%
                        </span>
                        <span className="font-mono tabular-nums text-ink-500 ml-1">
                          ({teacher.semester1.allocatedMinutes}/
                          {teacher.semester1.availableMinutes} min)
                        </span>
                      </div>
                    </div>

                    {/* Semester 1 Progress Bar */}
                    <div className="w-full bg-ink-200 rounded-full h-2 mb-1">
                      <div className={`h-2 rounded-full transition-all ${getUtilizationColor(teacher.semester1.utilization)}`} style={{
                  width: `${Math.min(100, teacher.semester1.utilization)}%`
                }} />
                    </div>

                    <div className="text-xs text-ink-500 mb-2">
                      <div className="flex justify-between">
                        <span>
                          Remaining:{' '}
                          {Math.max(0, teacher.semester1.availableMinutes - teacher.semester1.allocatedMinutes)}{' '}
                          min
                        </span>
                        {teacher.semester1.allowanceMinutes > 0 && <span className="font-mono tabular-nums text-navy-600">
                            -{teacher.semester1.allowanceMinutes} allowances
                          </span>}
                      </div>
                    </div>

                    {/* Show allowance details if enabled */}
                    {showAllowanceDetails && teacher.semester1.allowances.length > 0 && <div className="bg-navy-50 rounded p-2 mb-2">
                        <div className="text-xs font-medium text-navy-700 mb-1">
                          Allowances:
                        </div>
                        {teacher.semester1.allowances.map((allowance, idx) => <div key={`${teacher.id || teacher.staff_id || teacher.initial}-s1-allowance-${idx}`} className="text-xs text-navy-600 flex justify-between">
                            <span>{allowance.name}</span>
                            <span className="font-mono tabular-nums font-medium">{allowance.minutes} min</span>
                          </div>)}
                      </div>}

                    {/* Semester 1 Assignments */}
                    {teacher.semester1.assignments.length > 0 && <div className="space-y-1 max-h-20 overflow-y-auto">
                        {teacher.semester1.assignments.map((assignment, idx) => <div key={`${teacher.id || teacher.staff_id || teacher.initial}-s1-assignment-${idx}`} className="text-xs text-ink-500 flex justify-between items-center bg-white px-2 py-1 rounded">
                              <span className="truncate flex-1 mr-2">
                                {assignment.className.length > 25 ? `${assignment.className.substring(0, 25)}...` : assignment.className}
                              </span>
                              <span className="font-mono tabular-nums text-xs font-medium text-navy-600">
                                {assignment.lineId}
                              </span>
                            </div>)}
                      </div>}
                  </div>}

                {/* Semester 2 Section */}
                {(effectiveSemester === 'both' || effectiveSemester === 'semester2') && <div>
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-xs font-medium text-ink-700">
                        {effectiveSemester === 'both' ? 'Semester 2' : 'Current Load'}
                      </div>
                      <div className="text-xs">
                        <span className={`font-mono tabular-nums ${getUtilizationTextColor(teacher.semester2.utilization)}`}>
                          {teacher.semester2.utilization}%
                        </span>
                        <span className="font-mono tabular-nums text-ink-500 ml-1">
                          ({teacher.semester2.allocatedMinutes}/
                          {teacher.semester2.availableMinutes} min)
                        </span>
                      </div>
                    </div>

                    {/* Semester 2 Progress Bar */}
                    <div className="w-full bg-ink-200 rounded-full h-2 mb-1">
                      <div className={`h-2 rounded-full transition-all ${getUtilizationColor(teacher.semester2.utilization)}`} style={{
                  width: `${Math.min(100, teacher.semester2.utilization)}%`
                }} />
                    </div>

                    <div className="text-xs text-ink-500 mb-2">
                      <div className="flex justify-between">
                        <span>
                          Remaining:{' '}
                          {Math.max(0, teacher.semester2.availableMinutes - teacher.semester2.allocatedMinutes)}{' '}
                          min
                        </span>
                        {teacher.semester2.allowanceMinutes > 0 && <span className="font-mono tabular-nums text-navy-600">
                            -{teacher.semester2.allowanceMinutes} allowances
                          </span>}
                      </div>
                    </div>

                    {/* Show allowance details if enabled */}
                    {showAllowanceDetails && teacher.semester2.allowances.length > 0 && <div className="bg-navy-50 rounded p-2 mb-2">
                        <div className="text-xs font-medium text-navy-700 mb-1">
                          Allowances:
                        </div>
                        {teacher.semester2.allowances.map((allowance, idx) => <div key={`${teacher.id || teacher.staff_id || teacher.initial}-s2-allowance-${idx}`} className="text-xs text-navy-600 flex justify-between">
                            <span>{allowance.name}</span>
                            <span className="font-mono tabular-nums font-medium">{allowance.minutes} min</span>
                          </div>)}
                      </div>}

                    {/* Semester 2 Assignments */}
                    {teacher.semester2.assignments.length > 0 && <div className="space-y-1 max-h-20 overflow-y-auto">
                        {teacher.semester2.assignments.map((assignment, idx) => <div key={`${teacher.id || teacher.staff_id || teacher.initial}-s2-assignment-${idx}`} className="text-xs text-ink-500 flex justify-between items-center bg-white px-2 py-1 rounded">
                              <span className="truncate flex-1 mr-2">
                                {assignment.className.length > 25 ? `${assignment.className.substring(0, 25)}...` : assignment.className}
                              </span>
                              <span className="font-mono tabular-nums text-xs font-medium text-navy-600">
                                {assignment.lineId}
                              </span>
                            </div>)}
                      </div>}
                  </div>}
              </div>)}

            {activeTeachers.length === 0 && <div className="text-center py-8 text-ink-500">
                <div className="text-sm">
                  {effectiveSemester === 'both' ? 'All teachers fully assigned in both semesters' : `All teachers fully assigned in ${effectiveSemester === 'semester1' ? 'Semester 1' : 'Semester 2'}`}
                </div>
                <div className="text-xs mt-1">Great job! 🎉</div>
              </div>}
          </div>
        </div>
      </div>

      {/* Fully Assigned Teachers (Collapsible) */}
      {fullyAssignedTeachers.length > 0 && <div className="border-t border-ink-200 flex-shrink-0">
          <button className="w-full p-4 text-left hover:bg-ink-50 flex justify-between items-center transition-colors" onClick={() => setShowFullyAssigned(!showFullyAssigned)}>
            <div>
              <h4 className="text-sm font-medium text-ink-700">
                Fully Assigned ({fullyAssignedTeachers.length})
              </h4>
              <p className="text-xs text-ink-500">
                {effectiveSemester === 'both' ? '100% capacity reached in both semesters' : `100% capacity reached in ${effectiveSemester === 'semester1' ? 'Semester 1' : 'Semester 2'}`}
              </p>
            </div>
            <div className={`transform transition-transform ${showFullyAssigned ? 'rotate-180' : ''}`}>
              <svg className="h-4 w-4 text-ink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {showFullyAssigned && <div className="px-4 pb-4 max-h-48 overflow-y-auto">
              <div className="space-y-2">
                {fullyAssignedTeachers.map((teacher, index) => <div key={`fully-assigned-${teacher.id || teacher.staff_id || teacher.initial || index}`} className="border rounded p-3 bg-ok-bg border-ok-border hover:bg-ok-bg transition-colors">
                    <div className="flex justify-between items-center mb-1">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink-900 truncate flex items-center gap-2">
                          {teacher.staff_id || teacher.initial} - {teacher.fullName}
                          {hasAdditionalComments(teacher.staff_id || teacher.initial) && (
                            <button
                              onClick={() => showAdditionalComments(teacher.staff_id || teacher.initial, teacher.fullName)}
                              className="p-1 hover:bg-navy-100 rounded-full transition-colors group"
                              title="View additional comments"
                            >
                              <MessageSquare className="h-4 w-4 text-navy-600 group-hover:text-navy-700" />
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-ink-500">
                          {teacher.department}
                          {teacher.totalGrids > 1 && (
                            <span className="font-mono tabular-nums ml-2 px-2 py-0.5 bg-navy-100 text-navy-700 rounded-full text-xs">
                              {teacher.totalGrids} grids
                            </span>
                          )}
                        </div>
                        {teacher.gridBreakdown && teacher.gridBreakdown.length > 1 && (
                          <div className="text-xs text-ink-500 mt-1">
                            <div className="flex flex-wrap gap-1">
                              {teacher.gridBreakdown.map((gridData, idx) => (
                                <span key={idx} className="px-1 py-0.5 bg-ink-100 rounded text-xs">
                                  {gridData.gridName}: {gridData.semester1Minutes + gridData.semester2Minutes}min
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Compact Semester Stats */}
                    <div className="flex justify-between text-xs mt-1">
                      {(effectiveSemester === 'both' || effectiveSemester === 'semester1') && <div>
                          <span className="text-ink-500">
                            {effectiveSemester === 'both' ? 'S1:' : 'Load:'}
                          </span>
                          <span className={`font-mono tabular-nums ml-1 font-medium ${getUtilizationTextColor(teacher.semester1.utilization)}`}>
                            {teacher.semester1.utilization}%
                          </span>
                          {teacher.semester1.allowanceMinutes > 0 && <span className="font-mono tabular-nums ml-1 text-navy-600 text-xs">
                              (-{teacher.semester1.allowanceMinutes})
                            </span>}
                        </div>}

                      {(effectiveSemester === 'both' || effectiveSemester === 'semester2') && <div>
                          <span className="text-ink-500">
                            {effectiveSemester === 'both' ? 'S2:' : 'Load:'}
                          </span>
                          <span className={`font-mono tabular-nums ml-1 font-medium ${getUtilizationTextColor(teacher.semester2.utilization)}`}>
                            {teacher.semester2.utilization}%
                          </span>
                          {teacher.semester2.allowanceMinutes > 0 && <span className="font-mono tabular-nums ml-1 text-navy-600 text-xs">
                              (-{teacher.semester2.allowanceMinutes})
                            </span>}
                        </div>}

                      <div className="font-mono tabular-nums text-ink-500">
                        {effectiveSemester === 'both' ? `${teacher.semester1.assignments.length + teacher.semester2.assignments.length} assignments` : effectiveSemester === 'semester1' ? `${teacher.semester1.assignments.length} assignments` : `${teacher.semester2.assignments.length} assignments`}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>}
        </div>}

      {/* Additional Comments Modal */}
      <AdditionalCommentsModal
        isOpen={commentsModal.isOpen}
        onClose={closeCommentsModal}
        staffName={commentsModal.staffName}
        staffInitial={commentsModal.staffInitial}
        additionalComments={commentsModal.comments}
      />
    </div>;
};
export default TeacherLoadPanel;